import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Icon } from "@project/ui";
import { authClient } from "@apps/dashboard/lib/auth-client";
import { AuthCenterLayout } from "@apps/dashboard/components/auth/AuthCenterLayout";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { translateAuthError } from "@apps/dashboard/features/auth/map-auth-error";
import { dashboardPaths } from "@apps/dashboard/lib/routes";

const MIN_PASSWORD = 8;

export default function ResetPasswordPage(): JSX.Element {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") ?? "", [params]);
  const queryError = useMemo(() => params.get("error"), [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const tokenMissing = token.length === 0;
  const tokenRejected = queryError === "INVALID_TOKEN";

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError(t("dashboardUi.auth.invalidResetToken"));
      return;
    }
    if (password.length < MIN_PASSWORD) {
      setError(t("dashboardUi.auth.errors.passwordTooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("validation.passwordMismatch"));
      return;
    }
    setSubmitting(true);
    try {
      const { error: err } = await authClient.resetPassword({
        newPassword: password,
        token,
      });
      if (err) {
        setError(translateAuthError(err, t));
        return;
      }
      setDone(true);
    } catch (unknownErr) {
      setError(translateAuthError(unknownErr, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCenterLayout>
      <div className="relative z-10 w-full max-w-[480px]">
        <div className="rounded-xl border border-outline-variant/15 bg-surface-container-lowest p-10 shadow-sm">
          <h1 className="mb-2 font-headline text-headline-sm font-bold text-primary">{t("dashboardUi.auth.resetTitle")}</h1>
          <p className="mb-8 text-body-md text-on-surface-variant">{t("dashboardUi.auth.resetSubtitle")}</p>

          {(tokenMissing || tokenRejected) && !done ? (
            <div className="mb-6 rounded-lg bg-error/10 px-4 py-4 text-body-md text-error outline outline-1 outline-error/20" role="alert">
              {t("dashboardUi.auth.invalidResetToken")}
            </div>
          ) : null}
          {tokenMissing && !done ? (
            <p className="mb-6 text-center text-body-md">
              <Link to={dashboardPaths.forgotPassword} className="font-semibold text-primary hover:underline">
                {t("dashboardUi.auth.forgotLink")}
              </Link>
            </p>
          ) : null}

          {done ? (
            <div className="rounded-lg bg-secondary-container/20 px-4 py-4 text-body-md text-on-secondary-container" role="status">
              {t("dashboardUi.auth.resetSuccess")}
            </div>
          ) : null}

          {!done && !tokenMissing && !tokenRejected ? (
            <>
              {error ? (
                <div className="mb-6 rounded-lg bg-error/10 px-4 py-3 text-body-md text-error outline outline-1 outline-error/20" role="alert">
                  {error}
                </div>
              ) : null}
              <form className="space-y-5" onSubmit={onSubmit}>
                <div className="space-y-1.5">
                  <label htmlFor="reset-password" className="ml-1 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                    {t("dashboardUi.auth.passwordLabel")}
                  </label>
                  <input
                    id="reset-password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(ev) => setPassword(ev.target.value)}
                    placeholder={t("dashboardUi.auth.passwordPlaceholder")}
                    className="h-12 w-full rounded-lg border-none bg-surface-container-low px-4 text-on-surface outline-none focus:bg-surface-container-lowest focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="reset-confirm" className="ml-1 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                    {t("dashboardUi.auth.confirmPasswordLabel")}
                  </label>
                  <input
                    id="reset-confirm"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirm}
                    onChange={(ev) => setConfirm(ev.target.value)}
                    placeholder={t("dashboardUi.auth.passwordPlaceholder")}
                    className="h-12 w-full rounded-lg border-none bg-surface-container-low px-4 text-on-surface outline-none focus:bg-surface-container-lowest focus:ring-1 focus:ring-primary"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting || !token}
                  className="primary-gradient h-12 w-full rounded-lg font-headline font-semibold text-on-primary shadow-md disabled:opacity-60"
                >
                  {submitting ? t("common.loading") : t("dashboardUi.auth.saveNewPassword")}
                </button>
              </form>
            </>
          ) : null}

          <p className="mt-8 text-center">
            <Link
              to={dashboardPaths.login}
              className="inline-flex items-center gap-2 text-body-md font-semibold text-primary hover:underline"
            >
              <Icon name="ArrowLeft" className="size-4" strokeWidth={1.5} />
              {t("dashboardUi.auth.backToLogin")}
            </Link>
          </p>
        </div>
      </div>
    </AuthCenterLayout>
  );
}
